<script lang="ts" setup>
// PLAT-3131 — the "Why verify through my bank?" explainer, opened from the Plaid priming step.
//
// Dismissing this answers a question and nothing more: it must NOT resolve the Plaid offer, mark
// the attempt declined, or exit the task. The participant lands back on the priming step exactly
// as they left it. That is the whole reason this is a separate surface rather than a branch in
// the flow — asking why should never cost someone their place.
//
// Design shows a bottom sheet with a drag handle. The UI library has no sheet primitive, so this
// uses the app-modal idiom every other modal in this MFE uses; on mobile it presents from the
// bottom already. Worth a look from Design before release — see the PR description.
import { computed } from 'vue';

const props = defineProps<{
    modelValue: boolean;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
    (e: 'continue'): void;
}>();

const isOpen = computed({
    get: () => props.modelValue,
    set: (value: boolean) => emit('update:modelValue', value),
});

const close = () => {
    isOpen.value = false;
};

const onContinue = () => {
    // Close first so the sheet is gone behind Plaid's own overlay rather than stacked under it.
    isOpen.value = false;
    emit('continue');
};
</script>

<template>
    <app-modal
        :is-open="isOpen"
        center-content
        @onClose="close"
    >
        <app-modal-layout
            title="Why verify through my bank?"
            show-close
            :center-content="false"
            footer-format="center"
            @onClose="close"
        >
            <p class="plaid-why-verify__paragraph">
                Verifying your account through your bank is one of the ways we protect your closing
                from payment fraud. It confirms the account is yours before any money moves.
            </p>

            <p class="plaid-why-verify__paragraph">
                Plaid handles the login. Your credentials are never shared with your title company
                or CertifID.
            </p>

            <template #footer-buttons>
                <app-button
                    expanded
                    size="large"
                    data-id="plaid-why-verify-continue"
                    @onClick="onContinue"
                >
                    Continue to Plaid
                </app-button>
            </template>
        </app-modal-layout>
    </app-modal>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.plaid-why-verify__paragraph {
    margin: 0 0 1rem;
    text-align: left;

    &:last-of-type {
        margin-bottom: 0;
    }
}
</style>
